import { Router, UrlTree } from '@angular/router';
import { Uuid } from '@hidat/huijs-common';
import { inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { InstanceManagerService } from '../resources/instance-manager.service';
import { StationFeatures, StationManagerService } from '../resources/station-manager.service';
import { DeviceStoreService } from '../services/device-store.service';
import { SplixerLayoutControlService } from '../services/layout-control';

/**
 * Loads the given station and instance.
 * Wrap this with the actual guard function that passes in the current station and instance (normally pulled from
 * the DeviceStore).
 * @param defaultStation
 * @param instanceId
 * @param listenerId
 * @param features
 */
export const loadStationAndInstance = (defaultStation: Uuid, instanceId: Uuid | undefined, listenerId: Uuid | undefined, features: StationFeatures): Observable<boolean | UrlTree> => {
  const station = inject(StationManagerService);
  const instanceManager = inject(InstanceManagerService);
  const router = inject(Router);
  const title = inject(Title);

  try {
    if (station.isLoaded()) {
      return of(true);
    }
    console.log('Loading station information...');
    const errorTree = router.parseUrl('station_error');
    const station$ = station.loadStation(defaultStation, features).pipe(map((playerStation) => {
      if (playerStation) {
        title.setTitle(station.stationCode);
        return true;
      }
      console.error('Unable to load station!');
      return errorTree;
    }), catchError(err => {
      console.error('Load Error: ', err.message);
      return of(errorTree);
    }));

    let instance$: Observable<boolean>;
    if (instanceId || listenerId) {
      instance$ = instanceManager.loadInstance(defaultStation, instanceId, listenerId);
    } else {
      return station$;
    }

    return forkJoin({
      station: station$,
      instance: instance$
    }).pipe(map((results) => {
      // All we need is the stations - if the instance didn't load, it will have been cleared and will be recreated when the user plays something
      if (results.station) {
        return true;
      }
      console.warn('There was a problem loading the station information');
      return errorTree;
    }), catchError(err => {
      console.error('Loading Error: ', err.message);
      return of(errorTree);
    }));
  } catch (err) {
    alert('Error loading the application.');
    return of(false);
  }

};

/**
 * Use in provideAppInitializer to load the station service from the backend.
 * Loads instance information from the local storage
 */
export function loadStationInitializer(
  stationUid: Uuid,
  stationCode: string,
  features: StationFeatures
): Observable<boolean> {
  const deviceStore = inject(DeviceStoreService);
  const layoutService = inject(SplixerLayoutControlService);

  deviceStore.loadSettings(stationCode);
  let instanceId: Uuid | undefined;
  if (deviceStore.hasInstance()) {
    instanceId = deviceStore.instanceId;
  }
  const listenerId = deviceStore.listenerId();
  const theme = deviceStore.getPlayerSetting('theme');
  if (theme) {
    layoutService.setTheme(theme);
  }

  return loadStationAndInstance(stationUid, instanceId, listenerId, features).pipe(
    map((r) => {
      if (typeof r === 'boolean') {
        return r;
      }
      return false;
    })
  );
}
